import { useAuth0 } from '@auth0/auth0-react';
import { useState, useEffect } from 'react';
import { UserProfile } from '../components/UserProfile';
import { api, NotificationPrefs, PoolTypesResponse } from '../services/api';

interface User {
  id: string;
  email: string;
  username?: string;
  role?: string;
  createdAt: string;
  updatedAt: string;
}

// iOS-style Toggle Switch Component
function ToggleSwitch({
  enabled,
  onChange,
  disabled = false,
}: {
  enabled: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      style={{
        width: '51px',
        height: '31px',
        borderRadius: '15.5px',
        backgroundColor: enabled ? '#34C759' : '#E9E9EB',
        border: 'none',
        padding: '2px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background-color 0.2s ease-in-out',
        position: 'relative',
        flexShrink: 0,
      }}
      role="switch"
      aria-checked={enabled}
    >
      <span
        style={{
          display: 'block',
          width: '27px',
          height: '27px',
          borderRadius: '50%',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.1)',
          transform: enabled ? 'translateX(20px)' : 'translateX(0)',
          transition: 'transform 0.2s ease-in-out',
        }}
      />
    </button>
  );
}

export function Profile() {
  const { getAccessTokenSilently } = useAuth0();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPrefs>({
    deadlineReminder: true,
    poolLocked: true,
    scoreUpdates: false,
    broadcasts: true,
    subscribedPoolTypes: [],
  });
  const [poolTypes, setPoolTypes] = useState<PoolTypesResponse | null>(null);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsMessage, setPrefsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadUser();
    loadNotificationPrefs();
  }, []);

  const loadUser = async () => {
    try {
      const token = await getAccessTokenSilently();
      const userData = await api.getCurrentUser(token);
      setUser(userData);
    } catch (err) {
      console.error('Failed to load user:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadNotificationPrefs = async () => {
    try {
      const token = await getAccessTokenSilently();
      const [prefs, types] = await Promise.all([
        api.getNotificationPreferences(token),
        api.getPoolTypes(token),
      ]);
      setNotificationPrefs(prefs);
      setPoolTypes(types);
    } catch (err) {
      console.error('Failed to load notification preferences:', err);
      // Use defaults if API fails
      setNotificationPrefs({
        deadlineReminder: true,
        poolLocked: true,
        scoreUpdates: false,
        broadcasts: true,
        subscribedPoolTypes: [],
      });
    } finally {
      setPrefsLoading(false);
    }
  };

  const handleTogglePref = async (key: 'deadlineReminder' | 'poolLocked' | 'scoreUpdates' | 'broadcasts') => {
    setPrefsSaving(true);
    setPrefsMessage(null);

    const newValue = !notificationPrefs[key];
    const previousPrefs = { ...notificationPrefs };
    const updatedPrefs = { ...notificationPrefs, [key]: newValue };

    // Optimistically update UI
    setNotificationPrefs(updatedPrefs);

    try {
      const token = await getAccessTokenSilently();
      await api.updateNotificationPreferences({ [key]: newValue }, token);
      setPrefsMessage({ type: 'success', text: 'Preferences saved!' });
      setTimeout(() => setPrefsMessage(null), 3000);
    } catch (err) {
      // Revert on error
      setNotificationPrefs(previousPrefs);
      setPrefsMessage({ type: 'error', text: 'Failed to save preferences' });
      console.error('Failed to update notification preferences:', err);
    } finally {
      setPrefsSaving(false);
    }
  };

  const handleTogglePoolType = async (poolType: string) => {
    setPrefsSaving(true);
    setPrefsMessage(null);

    const currentTypes = notificationPrefs.subscribedPoolTypes || [];
    const isSubscribed = currentTypes.includes(poolType);
    const newTypes = isSubscribed
      ? currentTypes.filter(t => t !== poolType)
      : [...currentTypes, poolType];

    const previousPrefs = { ...notificationPrefs };
    const updatedPrefs = { ...notificationPrefs, subscribedPoolTypes: newTypes };

    // Optimistically update UI
    setNotificationPrefs(updatedPrefs);

    try {
      const token = await getAccessTokenSilently();
      await api.updateNotificationPreferences({ subscribedPoolTypes: newTypes }, token);
      setPrefsMessage({ type: 'success', text: 'Preferences saved!' });
      setTimeout(() => setPrefsMessage(null), 3000);
    } catch (err) {
      // Revert on error
      setNotificationPrefs(previousPrefs);
      setPrefsMessage({ type: 'error', text: 'Failed to save preferences' });
      console.error('Failed to update notification preferences:', err);
    } finally {
      setPrefsSaving(false);
    }
  };

  const notificationOptions: Array<{
    key: 'deadlineReminder' | 'poolLocked' | 'scoreUpdates' | 'broadcasts';
    title: string;
    description: string;
    icon: JSX.Element;
  }> = [
    {
      key: 'deadlineReminder',
      title: 'Deadline Reminders',
      description: 'Get notified 24 hours before a pool entry deadline',
      icon: (
        <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      key: 'poolLocked',
      title: 'Pool Locked Notifications',
      description: 'Get notified when entries are locked and the pool begins',
      icon: (
        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
    },
    {
      key: 'scoreUpdates',
      title: 'Score Updates',
      description: 'Get notified when scores are updated after games',
      icon: (
        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      key: 'broadcasts',
      title: 'Announcements',
      description: 'Receive occasional announcements from ProPoolPicks (platform updates, new pools, etc.)',
      icon: (
        <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
          <p className="mt-2 text-gray-600">
            Manage your account settings and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Settings - Takes up 2 columns */}
          <div className="lg:col-span-2">
            <UserProfile />
          </div>

          {/* Account Information - Takes up 1 column */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4" style={{ color: '#111827' }}>Account Info</h2>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Email</p>
                    <p className="font-medium text-sm" style={{ color: '#111827' }}>{user?.email}</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Username</p>
                    <p className="font-medium text-sm" style={{ color: '#111827' }}>
                      {user?.username || (
                        <span className="text-gray-400 italic">Not set</span>
                      )}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Member Since</p>
                    <p className="font-medium text-sm" style={{ color: '#111827' }}>
                      {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : 'N/A'}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Role</p>
                    <p className="font-medium text-sm" style={{ color: '#111827' }}>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user?.role === 'ADMIN'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user?.role || 'USER'}
                      </span>
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Notification Preferences */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  <h2 className="text-xl font-bold" style={{ color: '#111827' }}>Notifications</h2>
                </div>
                {prefsSaving && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                    <span>Saving...</span>
                  </div>
                )}
              </div>

              {prefsMessage && (
                <div className={`mb-4 px-4 py-3 rounded-lg flex items-center gap-2 text-sm ${
                  prefsMessage.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {prefsMessage.type === 'success' ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                  {prefsMessage.text}
                </div>
              )}

              {prefsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="space-y-1">
                  {notificationOptions.map((option, index) => (
                    <div
                      key={option.key}
                      className={`flex items-center justify-between py-4 ${
                        index !== notificationOptions.length - 1 ? 'border-b border-gray-100' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3 flex-1 pr-4">
                        <div className="flex-shrink-0 mt-0.5">
                          {option.icon}
                        </div>
                        <div>
                          <p className="font-medium text-sm" style={{ color: '#111827' }}>
                            {option.title}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {option.description}
                          </p>
                        </div>
                      </div>
                      <ToggleSwitch
                        enabled={notificationPrefs[option.key]}
                        onChange={() => handleTogglePref(option.key)}
                        disabled={prefsSaving}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Sport-Specific Notifications */}
              {poolTypes && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-medium" style={{ color: '#111827' }}>
                        Pool Type Preferences
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {notificationPrefs.subscribedPoolTypes?.length === 0
                          ? 'Receiving notifications for all pool types'
                          : `Receiving notifications for ${notificationPrefs.subscribedPoolTypes?.length} pool type(s)`
                        }
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {poolTypes.poolTypes.map((poolType) => {
                      const isSubscribed = notificationPrefs.subscribedPoolTypes?.length === 0 ||
                        notificationPrefs.subscribedPoolTypes?.includes(poolType);
                      const label = poolTypes.labels[poolType] || poolType;

                      return (
                        <label
                          key={poolType}
                          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                            isSubscribed
                              ? 'bg-blue-50 border-blue-200'
                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          } ${prefsSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isSubscribed}
                              onChange={() => handleTogglePoolType(poolType)}
                              disabled={prefsSaving}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium" style={{ color: '#374151' }}>
                              {label}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  <p className="text-xs text-gray-400 mt-3">
                    Leave all unchecked to receive notifications for all pool types.
                    Check specific pools to only get notifications for those.
                  </p>
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  Emails sent from propoolpicks.com
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
